import i18n from '@cohort/components-xps/lib/i18n';
import type {CohortLinkConfig, CohortLinkUsageData} from '@cohort/shared/apps/cohort/perks/link';
import {defaultErrorMessage} from '@cohort/shared/models';
import {isCohortError} from '@cohort/shared/schema/common/errors';
import {
  buildLocalizationConfig,
  formatI18nLanguage,
  getLocalizedContentOrThrow,
} from '@cohort/shared/utils/localization';
import type {PerkUsageComponentProps} from '@cohort/wallet/apps/PerkIntegration';
import {usePerkAccess} from '@cohort/wallet/hooks/api/PerkAccesses';
import useNotify from '@cohort/wallet/hooks/notify';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import {doUsePerkAccess} from '@cohort/wallet/lib/Endpoints';
import React, {Fragment, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';

export const CohortLinkPerkUsageComponent: React.FC<PerkUsageComponentProps> = props => {
  const {
    perk,
    onClose,
    onPerkUsageSuccess,
    perkAccess: {id: perkAccessId},
  } = props;
  const merchant = useMerchantContext();
  const {t} = useTranslation('app-cohort', {
    keyPrefix: 'perkIntegrations.link.perkUsageComponent',
  });

  const config = perk.integration?.config as CohortLinkConfig;
  const notify = useNotify();
  const isMounted = useRef(false);

  const localizationConfig = buildLocalizationConfig(
    formatI18nLanguage(i18n.language),
    merchant.supportedLanguages,
    merchant.defaultLanguage
  );
  const localizedUrl = getLocalizedContentOrThrow(config.url, localizationConfig);

  const {data: perkAccess} = usePerkAccess(perkAccessId);

  useEffect(() => {
    if (isMounted.current || perkAccess === undefined) {
      return;
    }
    isMounted.current = true;
    // Open the URL in a new tab
    window.open(localizedUrl, '_blank');
    doUsePerkAccess(perkAccess.id, null, {} as CohortLinkUsageData)
      .then(() => {
        onPerkUsageSuccess();
      })
      .catch(err => {
        if (isCohortError(err, 'perk.usages-limit-reached')) {
          notify('error', t('maxUsagesReached'), {autoClose: false});
          return;
        }
        notify('error', defaultErrorMessage);
      });
    onClose();
  }, [localizedUrl, onPerkUsageSuccess, perk, perkAccess, notify, t, onClose]);

  return <Fragment />;
};
